/**
 * useVoiceInput — Web Speech API hook for voice-to-text input
 * Works in Chrome, Edge, and Android WebView (Capacitor).
 * Falls back gracefully on unsupported browsers.
 */
import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const SpeechRecognition = typeof window !== 'undefined'
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : null;

const SILENCE_TIMEOUT_MS = 5000;
const MAX_LISTEN_MS = 15000;

export default function useVoiceInput() {
  const { i18n } = useTranslation();
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const [isSupported] = useState(() => !!SpeechRecognition);

  const recognitionRef = useRef(null);
  const silenceTimerRef = useRef(null);
  const maxTimerRef = useRef(null);

  const cleanup = useCallback(() => {
    if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);
    if (maxTimerRef.current) clearTimeout(maxTimerRef.current);
    silenceTimerRef.current = null;
    maxTimerRef.current = null;
  }, []);

  const stopListening = useCallback(() => {
    cleanup();
    if (recognitionRef.current) {
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Already stopped
      }
    }
    setIsListening(false);
    setInterimTranscript('');
  }, [cleanup]);

  const startListening = useCallback(() => {
    if (!SpeechRecognition) {
      setError('Speech recognition not supported in this browser');
      return;
    }

    setError(null);
    setTranscript('');
    setInterimTranscript('');

    const recognition = new SpeechRecognition();
    recognitionRef.current = recognition;

    // Map i18n locale to BCP-47 language tag
    const locale = i18n.language || 'en';
    const langMap = {
      en: 'en-US', de: 'de-DE', es: 'es-ES',
      fr: 'fr-FR', it: 'it-IT', pt: 'pt-BR',
    };
    recognition.lang = langMap[locale] || `${locale}-${locale.toUpperCase()}`;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      // Max listening duration
      maxTimerRef.current = setTimeout(() => {
        stopListening();
      }, MAX_LISTEN_MS);
    };

    recognition.onresult = (event) => {
      // Reset silence timer on each result
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current);

      let final = '';
      let interim = '';
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (final) {
        setTranscript(final);
        setInterimTranscript('');
      } else {
        setInterimTranscript(interim);
      }

      // Silence timeout — stop after 5s of no new results
      silenceTimerRef.current = setTimeout(() => {
        stopListening();
      }, SILENCE_TIMEOUT_MS);
    };

    recognition.onerror = (event) => {
      cleanup();
      setIsListening(false);
      if (event.error === 'not-allowed') {
        setError('Microphone permission denied');
      } else if (event.error === 'no-speech') {
        setError('No speech detected. Try again.');
      } else if (event.error === 'network') {
        setError('Browser speech service is unavailable. Use Speak on the dashboard — it records on-device and transcribes on our servers.');
      } else if (event.error !== 'aborted') {
        setError(`Speech error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      cleanup();
      setIsListening(false);
      setInterimTranscript('');
    };

    try {
      recognition.start();
    } catch (e) {
      setError('Failed to start speech recognition');
      setIsListening(false);
    }
  }, [i18n.language, stopListening, cleanup]);

  const resetTranscript = useCallback(() => {
    setTranscript('');
    setInterimTranscript('');
    setError(null);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) { /* */ }
      }
    };
  }, [cleanup]);

  return {
    isListening,
    transcript,
    interimTranscript,
    error,
    isSupported,
    startListening,
    stopListening,
    resetTranscript,
  };
}
